import { Component } from '@theme/component';

// Debug marker (permet de vérifier que ce fichier est bien chargé)
try {
  if (window?.localStorage?.getItem('debugQuickAdd') === '1') {
    // eslint-disable-next-line no-console
    console.log('[QuickAdd] quick-add.js chargé');
  }
} catch (e) {
  // ignore
}

// Vérifier si fetchConfig existe, sinon le créer
const fetchConfig = (type = 'json', config = {}) => {
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };
  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }
  return {
    method: 'POST',
    headers: headers,
    body: config.body,
  };
};

export class QuickAddComponent extends Component {
  // Rendre panel optionnel car il peut ne pas exister
  requiredRefs = ['variantId', 'trigger'];

  /**
   * Debug helper (activable via localStorage.setItem('debugQuickAdd','1'))
   */
  #debug = (...args) => {
    try {
      if (window?.localStorage?.getItem('debugQuickAdd') === '1') {
        // eslint-disable-next-line no-console
        console.log('[QuickAdd]', ...args);
      }
    } catch (e) {
      // ignore
    }
  };

  /** @type {AbortController | null} */
  #clickOutsideController = null;

  /** @type {boolean} */
  #isMobile = false;

  connectedCallback() {
    super.connectedCallback();
    this.#checkMobile();
    window.addEventListener('resize', () => this.#checkMobile());
  }

  /**
   * Vérifie si on est sur mobile
   */
  #checkMobile() {
    const wasMobile = this.#isMobile;
    this.#isMobile = window.innerWidth <= 989;
    if (wasMobile !== this.#isMobile) {
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Nettoyer le listener si le composant est supprimé
    this.#clickOutsideController?.abort();
  }

  /**
   * Ferme le panneau et réaffiche le bouton
   */
  closePanel = () => {
    const { panel, trigger } = this.refs;

    if (panel && !panel.hasAttribute('hidden')) {
      panel.setAttribute('hidden', '');
      trigger?.removeAttribute('hidden');

      // Arrêter l'écoute des clics extérieurs
      this.#clickOutsideController?.abort();
      this.#clickOutsideController = null;
    }
  };

  /**
   * Gère les clics en dehors du composant
   */
  #handleClickOutside = (event) => {
    // Vérifier si le clic est en dehors du composant
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  };

  /**
   * Ouvre le panneau inline (affiche le panel, cache le trigger, écoute les clics dehors)
   */
  #openPanelInline = () => {
    const { panel, trigger } = this.refs;
    if (!panel || !panel.hasAttribute('hidden')) return;

    this.#closeAllOtherPanels();
    panel.removeAttribute('hidden');
    trigger?.setAttribute('hidden', '');

    this.#clickOutsideController?.abort();
    this.#clickOutsideController = new AbortController();
    setTimeout(() => {
      document.addEventListener('click', this.#handleClickOutside, {
        signal: this.#clickOutsideController.signal,
        capture: true
      });
    }, 0);
  };

  /**
   * Ferme tous les autres panneaux quick-add ouverts (sauf celui-ci)
   */
  #closeAllOtherPanels = () => {
    const allQuickAdds = document.querySelectorAll('quick-add-component');
    allQuickAdds.forEach((quickAdd) => {
      if (quickAdd !== this && typeof quickAdd.closePanel === 'function') {
        quickAdd.closePanel();
      }
    });
  };

  /**
   * Toggle le panneau (affiche/cache) ou ouvre le drawer sur mobile
   */
  togglePanel = (event) => {
    event.preventDefault();
    event.stopPropagation(); // Empêcher la propagation pour éviter de fermer immédiatement
    const { panel, trigger } = this.refs;

    this.#debug('togglePanel click', {
      isMobile: this.#isMobile,
      hasPanel: !!panel,
      productId: this.dataset.productId,
      productHandle: this.dataset.productHandle,
      selectedVariantId: this.refs?.variantId?.value || this.dataset.selectedVariantId,
      selectedVariantPrice: this.dataset.selectedVariantPrice,
    });

    // Si pas de panel (produit sans variantes), ajouter directement au panier
    if (!panel) {
      this.handleSubmit(event);
      return;
    }

    // Sur mobile : tenter d'ouvrir le drawer ; s'il est absent ou en erreur, ouvrir le panneau inline
    if (this.#isMobile) {
      const drawer = document.querySelector('quick-add-drawer');
      const productData = this.getProductData();

      this.#debug('mobile open attempt', {
        hasDrawer: !!drawer,
        hasOpenMethod: !!drawer && typeof drawer.open === 'function',
        productDataNull: !productData,
        productData,
        productData_price_type: typeof productData?.price,
        productData_compareAtPrice_type: typeof productData?.compareAtPrice,
      });

      if (drawer && typeof drawer.open === 'function' && productData) {
        try {
          drawer.open(productData).catch(() => {
            this.#openPanelInline();
          });
        } catch (e) {
          this.#openPanelInline();
        }
        return;
      }

      this.#openPanelInline();
      return;
    }

    // Comportement desktop (panneau)
    const isHidden = panel.hasAttribute('hidden');
    if (isHidden) {
      this.#openPanelInline();
    } else {
      this.closePanel();
    }
  };

  /**
   * Sélectionne une taille et ajoute au panier
   */
  selectSize = async (event) => {
    event.preventDefault();
    event.stopPropagation(); // Empêcher la propagation
    const button = event.target.closest('.quick-add__size-button');
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Mettre à jour l'input hidden avec le variant sélectionné
    const { variantId: variantInput } = this.refs;
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Feedback visuel : marquer la taille sélectionnée
    this.querySelectorAll('.quick-add__size-button.selected').forEach((btn) => btn.classList.remove('selected'));
    button.classList.add('selected');

    // Soumettre le formulaire pour ajouter au panier
    await this.handleSubmit(event);

    // Fermer le panneau une fois l'ajout au panier termine
    this.closePanel();
  };

  /**
   * Extrait les données du produit depuis la carte
   * @returns {Object|null} Données du produit ou null
   */
  getProductData = () => {
    try {
      const productCard = this.closest('.product-card') || this.closest('component-product-card');
      if (!productCard) {
        // Fallback : utiliser les data-attributes même sans productCard
        return {
          title: this.dataset.productTitle || '',
          price: this.dataset.selectedVariantPrice ? parseInt(this.dataset.selectedVariantPrice, 10) : null,
          selectedVariantId: this.dataset.selectedVariantId || null,
          productHandle: this.dataset.productHandle || null,
          productId: this.dataset.productId || null,
          color: this.dataset.selectedColor || null,
          colorImage: this.dataset.colorImage || null,
          colorHex: this.dataset.colorHex || null,
          sizeOptionIndex: this.dataset.sizeOptionIndex ? parseInt(this.dataset.sizeOptionIndex, 10) : null,
          sizeGuideImage: this.dataset.sizeGuideImage || null,
          variants: []
        };
      }

      // Récupérer le titre
      const titleElement = productCard.querySelector('.card__heading, .product-card__title, h3');
      const title = titleElement?.textContent?.trim() || this.dataset.productTitle || '';

      // Récupérer le prix depuis le DOM
      const priceElement = productCard.querySelector('.price, .product-card__price, .card-information .price');
      let price = null;
      let compareAtPrice = null;
      if (priceElement) {
        // Essayer plusieurs sélecteurs pour trouver le prix
        const priceText = priceElement.querySelector('.price__regular .jc-text-sm, .price__sale .jc-text-sm, .price-item--regular, .price-item--regular .jc-text-sm, .price__regular') ||
                          priceElement.querySelector('.price-item--regular') ||
                          priceElement.querySelector('.price');
        const compareText = priceElement.querySelector('.price__sale s, .price-item--compare, .price-item--compare s');

        if (priceText) {
          // Extraire le prix depuis le texte (format: "XX,XX €" ou "28,00€" ou "28€")
          const priceTextContent = priceText.textContent.trim();
          // Chercher un nombre avec éventuellement des décimales (virgule ou point)
          const priceMatch = priceTextContent.match(/[\d]+[,\.]?[\d]*/);
          if (priceMatch) {
            const priceValue = priceMatch[0].replace(',', '.');
            price = Math.round(parseFloat(priceValue) * 100); // Convertir en centimes
          }
        }
        if (compareText) {
          const compareTextContent = compareText.textContent.trim();
          const compareMatch = compareTextContent.match(/[\d]+[,\.]?[\d]*/);
          if (compareMatch) {
            const compareValue = compareMatch[0].replace(',', '.');
            compareAtPrice = Math.round(parseFloat(compareValue) * 100);
          }
        }
      }

      // Récupérer le variant sélectionné
      // Pour les produits complémentaires, le variant peut avoir changé via switch-color
      // Vérifier d'abord dans l'input hidden qui est mis à jour dynamiquement
      let selectedVariantId = this.refs.variantId?.value || this.dataset.selectedVariantId || null;

      // Récupérer le productHandle et le swatch sélectionné (pour les produits complémentaires)
      let productHandle = this.dataset.productHandle || null;
      let switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
      // Garder une référence au swatch sélectionné (fallbacks inclus)
      let colorSwatch = null;

      // Si pas trouvé dans productCard, chercher dans le document
      if (!switchColorSwatch) {
        const switchColorComponent = productCard.querySelector('switch-color-component');
        if (switchColorComponent) {
          switchColorSwatch = switchColorComponent.querySelector('.color-swatch.selected');
        }
      }

      if (switchColorSwatch) {
        colorSwatch = switchColorSwatch;
      }

      if (switchColorSwatch && switchColorSwatch.getAttribute('data-product-handle')) {
        productHandle = switchColorSwatch.getAttribute('data-product-handle');
      }

      let variants = [];
      let sizeOptionIndex = -1;

      // Essayer de récupérer depuis les data-attributes
      if (this.dataset.sizeOptionIndex) {
        sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
      }

      // 1. Essayer d'abord le script de switch-color qui contient tous les variants
      const switchDataScript = productCard.querySelector(`script[data-product-switch-data="${this.dataset.productId}"]`);

      if (switchDataScript) {
        try {
          const switchData = JSON.parse(switchDataScript.textContent);
          if (switchData.variants && Array.isArray(switchData.variants)) {
            variants = switchData.variants.map(v => ({
              id: v.id,
              price: v.price,
              compare_at_price: v.compare_at_price || 0,
              available: v.available !== false,
              options: v.options || [],
              featured_image: v.featured_image,
              image: v.image
            }));
          }
        } catch (e) {
          // Erreur silencieuse lors du parsing
        }
      }

      // 2. Sinon, parser tous les scripts application/ld+json et les combiner
      if (variants.length === 0) {
        const variantScripts = productCard.querySelectorAll('script[type="application/ld+json"]');
        if (variantScripts.length > 0) {
          variants = Array.from(variantScripts).map(script => {
            try {
              const data = JSON.parse(script.textContent);
              // Convertir le format LD+JSON en format variant
              return {
                id: data.id,
                price: data.price ? parseFloat(data.price.replace(/[^\d,.]/g, '').replace(',', '.')) * 100 : null,
                compare_at_price: data.compare_at_price ? parseFloat(data.compare_at_price.replace(/[^\d,.]/g, '').replace(',', '.')) * 100 : null,
                available: true, // Par défaut disponible si pas d'info
                options: [], // Les options ne sont pas dans le LD+JSON de la card
                featured_image: data.image
              };
            } catch (e) {
              return null;
            }
          }).filter(v => v !== null && v.id);
        }
      }

      // Si on a des variants et un selectedVariantId, vérifier que le variant existe toujours
      // Sinon, utiliser le premier variant disponible
      if (variants.length > 0) {
        if (selectedVariantId) {
          const variantExists = variants.find(v => v.id.toString() === selectedVariantId.toString());
          if (!variantExists) {
            // Le variant n'existe plus, utiliser le premier disponible
            selectedVariantId = variants[0].id;
          }
        } else {
          // Pas de variant sélectionné, utiliser le premier disponible
          selectedVariantId = variants[0].id;
        }
      }

      // Si pas de variants, essayer de les récupérer depuis l'API
      if (variants.length === 0) {
        // Utiliser productHandle déjà défini, ou le récupérer depuis le lien si pas encore défini
        if (!productHandle) {
          productHandle = this.dataset.productHandle || productCard.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^\/\?]+)/)?.[1];
        }
        if (productHandle) {
          // Récupérer la couleur depuis le swatch avant de retourner
          let color = null;
          let colorImage = null;
          let colorHex = null;

          if (switchColorSwatch) {
            color = switchColorSwatch.getAttribute('data-color-name') ||
                    switchColorSwatch.getAttribute('title')?.split(' - ')[0]?.trim() ||
                    switchColorSwatch.getAttribute('aria-label')?.split(' - ')[0]?.trim();


            const colorImageAttr = switchColorSwatch.getAttribute('data-color-image');
            if (colorImageAttr) {
              colorImage = colorImageAttr;
            } else {
              const swatchImage = switchColorSwatch.querySelector('.color-swatch-image img, img');
              if (swatchImage) {
                colorImage = swatchImage.src;
              }
            }

            const colorHexAttr = switchColorSwatch.getAttribute('data-color-hex');
            if (colorHexAttr) {
              colorHex = colorHexAttr;
            } else {
              colorHex = switchColorSwatch.style.backgroundColor || window.getComputedStyle(switchColorSwatch).backgroundColor;
            }
          }

          // Si le prix n'a pas été récupéré depuis le DOM, essayer de le récupérer depuis le variant sélectionné
          // Pour les produits complémentaires, le prix dans le DOM devrait être mis à jour par switch-color
          // Mais si ce n'est pas le cas, on utilisera le prix du variant sélectionné si disponible
          let finalPrice = price;
          let finalCompareAtPrice = compareAtPrice;

          // Si pas de prix depuis le DOM, essayer depuis le data-attribute (mais c'est statique)
          if (!finalPrice) {
            finalPrice = this.dataset.selectedVariantPrice ? parseInt(this.dataset.selectedVariantPrice, 10) : null;
          }

          const result = {
            title,
            price: finalPrice,
            compareAtPrice: finalCompareAtPrice,
            selectedVariantId,
            productHandle,
            productId: switchColorSwatch?.getAttribute('data-product-id') || this.dataset.productId,
            color: color || this.dataset.selectedColor || null,
            colorImage: colorImage || this.dataset.colorImage || null,
            colorHex: colorHex || this.dataset.colorHex || null,
            sizeOptionIndex: sizeOptionIndex >= 0 ? sizeOptionIndex : null,
            sizeGuideImage: this.dataset.sizeGuideImage || null,
            variants: [] // Sera rempli par le drawer si nécessaire
          };

          // On retourne les données disponibles et on laissera le drawer récupérer les variants
          return result;
        }
      }

      // Récupérer la couleur sélectionnée directement depuis le DOM (pas depuis data-attribute)
      // Essayer d'abord depuis le script JSON préchargé, sinon depuis le DOM
      let color = null;
      let colorImage = null;
      let colorHex = null;
      let selectedColor = null; // Pour le filtrage des variants

      // Vérifier le type de switch dans le JSON
      let switchType = null;
      // Réutiliser switchDataScript déjà déclaré plus haut
      if (switchDataScript) {
        try {
          const switchData = JSON.parse(switchDataScript.textContent);
          switchType = switchData.switchType;

          // Pour les variants, chercher dans le JSON
          if (switchType === 'variant' && selectedVariantId && switchData.variants && Array.isArray(switchData.variants)) {
            const selectedVariantData = switchData.variants.find(v => v.id.toString() === selectedVariantId.toString());
            if (selectedVariantData) {
              // Utiliser les données de couleur depuis le JSON si disponibles
              if (selectedVariantData.color_name) {
                color = selectedVariantData.color_name;
                selectedColor = selectedVariantData.options && selectedVariantData.options.length > 0
                  ? selectedVariantData.options[0] // Utiliser la première option (couleur) pour le filtrage
                  : color;
              }
              if (selectedVariantData.color_image) {
                colorImage = selectedVariantData.color_image;
              }
              if (selectedVariantData.color_hex) {
                colorHex = selectedVariantData.color_hex;
              }
            }
          }
          // Pour les produits complémentaires, le JSON ne contient pas les infos de couleur
          // On utilisera le fallback DOM ci-dessous
        } catch (e) {
          // Erreur silencieuse lors du parsing
        }
      }

      // Fallback : récupérer depuis le DOM si pas trouvé dans le JSON
      // (toujours nécessaire pour les produits complémentaires)
      // Réutiliser switchColorSwatch déjà trouvé plus haut, ou le chercher si pas encore trouvé
      if (!switchColorSwatch) {
        switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
        if (!switchColorSwatch) {
          const switchColorComponent = productCard.querySelector('switch-color-component');
          if (switchColorComponent) {
            switchColorSwatch = switchColorComponent.querySelector('.color-swatch.selected');
          }
        }
      }

      // Toujours récupérer la couleur depuis le swatch si disponible (même si color est déjà défini depuis le JSON)
      // Car pour les produits complémentaires, le JSON ne contient pas les infos de couleur
      // Réutiliser switchColorSwatch déjà trouvé plus haut, ou le chercher si pas encore trouvé
      if (!switchColorSwatch) {
        switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
        if (!switchColorSwatch) {
          const switchColorComponent = productCard.querySelector('switch-color-component');
          if (switchColorComponent) {
            switchColorSwatch = switchColorComponent.querySelector('.color-swatch.selected');
          }
        }
      }

      if (switchColorSwatch) {
        // Utiliser data-color-name en priorité (depuis le metafield shopify.color-pattern)
        const colorFromSwatch = switchColorSwatch.getAttribute('data-color-name') ||
                                switchColorSwatch.getAttribute('title')?.split(' - ')[0]?.trim() ||
                                switchColorSwatch.getAttribute('aria-label')?.split(' - ')[0]?.trim();

        if (colorFromSwatch) {
          color = colorFromSwatch; // Écraser la couleur du JSON avec celle du swatch
          selectedColor = color; // Utiliser le nom de la couleur pour le filtrage
        }

        // Récupérer l'image depuis data-color-image ou depuis l'img dans le swatch
        if (!colorImage) {
          const colorImageAttr = switchColorSwatch.getAttribute('data-color-image');
          if (colorImageAttr) {
            colorImage = colorImageAttr;
          } else {
            const swatchImage = switchColorSwatch.querySelector('.color-swatch-image img, img');
            if (swatchImage) {
              colorImage = swatchImage.src;
            }
          }
        }

        // Récupérer la couleur hex depuis data-color-hex ou depuis le style
        if (!colorHex) {
          const colorHexAttr = switchColorSwatch.getAttribute('data-color-hex');
          if (colorHexAttr) {
            colorHex = colorHexAttr;
          } else {
            colorHex = switchColorSwatch.style.backgroundColor || window.getComputedStyle(switchColorSwatch).backgroundColor;
          }
        }
      } else {
        // Fallback : chercher d'autres types de swatches si switchColorSwatch n'existe pas
        const variantColorSwatch = productCard.querySelector('.colorSwatch.selected, .colorSwatchContent[data-variant-id].selected');
        if (variantColorSwatch) {
          // Conserver le swatch pour l'envoyer au drawer si besoin
          colorSwatch = variantColorSwatch.closest('.colorSwatch') || variantColorSwatch;
          const swatchContent = variantColorSwatch.querySelector('.colorSwatchContent') || variantColorSwatch;
          const colorTitle = swatchContent.getAttribute('title') || swatchContent.getAttribute('aria-label');
          if (colorTitle && !color) {
            color = colorTitle.split(' - ')[0].trim();
            selectedColor = color;
          }
          if (!colorImage) {
            colorImage = swatchContent.style.backgroundImage?.match(/url\(['"]?([^'"]+)['"]?\)/)?.[1];
          }
          if (!colorHex) {
            colorHex = swatchContent.style.backgroundColor || window.getComputedStyle(swatchContent).backgroundColor;
          }
        }
      }


      // Filtrer les variants par couleur pour n'afficher que les tailles de la couleur sélectionnée (évite doublons mobile)
      if (variants.length > 0) {
        let colorOptionIndex = this.dataset.colorOptionIndex != null ? parseInt(this.dataset.colorOptionIndex, 10) : -1;
        if (colorOptionIndex < 0 && sizeOptionIndex >= 0 && variants[0]?.options?.length === 2) {
          colorOptionIndex = sizeOptionIndex === 0 ? 1 : 0;
        }
        if (colorOptionIndex < 0 && variants[0]?.options?.length >= 1) {
          colorOptionIndex = 0;
        }
        const selectedVariantForColor = selectedVariantId ? variants.find(v => v.id.toString() === selectedVariantId.toString()) : variants[0];
        const selectedColorValue = selectedVariantForColor?.options && colorOptionIndex >= 0 && colorOptionIndex < selectedVariantForColor.options.length
          ? selectedVariantForColor.options[colorOptionIndex]
          : selectedColor;
        if (colorOptionIndex >= 0 && selectedColorValue != null) {
          variants = variants.filter(v => v.options && v.options[colorOptionIndex] === selectedColorValue);
        }
      }

      // Récupérer le guide des tailles
      const sizeGuideImage = this.dataset.sizeGuideImage || null;


      // S'assurer qu'on utilise le productHandle depuis le swatch si disponible
      const finalProductHandle = productHandle || this.dataset.productHandle || null;

      // Utiliser le prix du variant sélectionné en priorité si disponible
      const selectedVariant = selectedVariantId && variants.length > 0
        ? variants.find(v => v.id.toString() === selectedVariantId.toString())
        : null;

      const finalPrice = selectedVariant?.price || price || null;
      const finalCompareAtPrice = selectedVariant?.compare_at_price || compareAtPrice || null;


      const result = {
        title,
        price: finalPrice,
        compareAtPrice: finalCompareAtPrice,
        selectedVariantId,
        productHandle: finalProductHandle,
        productId: switchColorSwatch?.getAttribute('data-product-id') || this.dataset.productId || null,
        color,
        colorImage,
        colorHex,
        colorSwatch: colorSwatch ? colorSwatch.outerHTML : null,
        sizeOptionIndex: sizeOptionIndex >= 0 ? sizeOptionIndex : (variants[0]?.options?.length > 0 ? variants[0].options.length - 1 : -1),
        sizeGuideImage,
        variants: variants.map(v => ({
          id: v.id,
          price: v.price,
          compare_at_price: v.compare_at_price,
          available: v.available !== false,
          options: v.options || [],
          size: v.options && v.options.length > 0 ? v.options[v.options.length - 1] : null
        }))
      };

      return result;
    } catch (error) {
      return null;
    }
  };

  /**
   * Met à jour les variants disponibles dans le quick-add
   * @param {Array} variants - Liste des variants disponibles pour la couleur sélectionnée
   * @param {string|number} selectedVariantId - ID du variant actuellement sélectionné
   * @param {number} [sizeOptionIndex] - Index de l'option de taille dans les options du variant (-1 pour auto-détecter)
   */
  updateVariants = (variants, selectedVariantId, sizeOptionIndex = -1) => {

    // Récupérer le panel depuis les refs ou depuis le querySelector
    const panel = this.refs?.panel || this.querySelector('.quick-add__panel');
    const variantInput = this.refs?.variantId;

    // Mettre à jour le drawer si ouvert sur mobile
    if (this.#isMobile) {
      const drawer = document.querySelector('quick-add-drawer');
      // Vérifier si le drawer est ouvert (is-open) ou visible (pas hidden)
      const isDrawerOpen = drawer && (
        drawer.classList.contains('is-open') ||
        !drawer.hasAttribute('hidden') ||
        document.body.classList.contains('quick-add-drawer-open')
      );

      if (drawer && isDrawerOpen) {
        // Attendre un peu pour que switch-color ait fini de mettre à jour la carte
        setTimeout(() => {
          const productCard = this.closest('.product-card') || this.closest('component-product-card');
          if (!productCard) {
            return;
          }

          // Trouver le variant sélectionné
          const selectedVariant = variants.find(v => v.id.toString() === selectedVariantId?.toString());

          // Récupérer les données depuis le DOM mis à jour par switch-color
          // 1. Titre depuis la carte mise à jour
          const titleElement = productCard.querySelector('.card__heading, .product-card__title, h3, h2');
          const title = titleElement?.textContent?.trim() || this.dataset.productTitle || '';

          // 2. ProductHandle depuis le swatch sélectionné ou depuis le titre link
          const switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
          const productHandle = switchColorSwatch?.getAttribute('data-product-handle') ||
                                productCard.querySelector('a[href*="/products/"]')?.href?.match(/\/products\/([^\/\?]+)/)?.[1] ||
                                this.dataset.productHandle;

          // 3. Couleur depuis le swatch sélectionné
          let color = null;
          let colorImage = null;
          let colorHex = null;

          if (switchColorSwatch) {
            color = switchColorSwatch.getAttribute('data-color-name') ||
                    switchColorSwatch.getAttribute('title')?.split(' - ')[0]?.trim() ||
                    switchColorSwatch.getAttribute('aria-label')?.split(' - ')[0]?.trim();

            const colorImageAttr = switchColorSwatch.getAttribute('data-color-image');
            if (colorImageAttr) {
              colorImage = colorImageAttr;
            } else {
              const swatchImage = switchColorSwatch.querySelector('.color-swatch-image img, img');
              if (swatchImage) {
                colorImage = swatchImage.src;
              }
            }

            const colorHexAttr = switchColorSwatch.getAttribute('data-color-hex');
            if (colorHexAttr) {
              colorHex = colorHexAttr;
            } else {
              colorHex = switchColorSwatch.style.backgroundColor || window.getComputedStyle(switchColorSwatch).backgroundColor;
            }
          }

          // 4. Prix depuis le variant sélectionné
          const price = selectedVariant?.price || null;
          const compareAtPrice = selectedVariant?.compare_at_price || null;

          // 5. Guide des tailles (récupérer depuis le data-attribute ou depuis le DOM)
          const sizeGuideImage = this.dataset.sizeGuideImage || null;

          // Construire les données du produit
          const productData = {
            title,
            price,
            compareAtPrice,
            selectedVariantId,
            productHandle,
            productId: switchColorSwatch?.getAttribute('data-product-id') || this.dataset.productId,
            color,
            colorImage,
            colorHex,
            sizeOptionIndex: sizeOptionIndex >= 0 ? sizeOptionIndex : (variants[0]?.options?.length > 0 ? variants[0].options.length - 1 : -1),
            sizeGuideImage,
            variants: variants.map(v => ({
              id: v.id,
              price: v.price,
              compare_at_price: v.compare_at_price,
              available: v.available !== false,
              options: v.options || [],
              size: v.options && sizeOptionIndex >= 0 && sizeOptionIndex < v.options.length ? v.options[sizeOptionIndex] : null
            }))
          };

          // Mettre à jour le drawer avec toutes les données
          drawer.populate(productData);
        }, 150);
      }
    }

    // Si pas de panneau (produit sans variantes), rien à faire
    if (!panel) {
      return;
    }

    // Trouver l'index de l'option de taille si non fourni
    if (sizeOptionIndex < 0 && variants.length > 0 && variants[0].options) {
      // Par défaut, on suppose que c'est la dernière option (généralement le cas)
      sizeOptionIndex = variants[0].options.length - 1;
    }

    // Trouver le conteneur des boutons de taille
    const sizesContainer = panel.querySelector('.quick-add__sizes');
    if (!sizesContainer) {
      return;
    }

    // Vider le conteneur
    sizesContainer.innerHTML = '';

    // Créer les boutons pour chaque variant disponible
    variants.forEach((variant) => {
      if (!variant.options || sizeOptionIndex < 0 || sizeOptionIndex >= variant.options.length) return;

      const sizeValue = variant.options[sizeOptionIndex];
      const isSelected = variant.id.toString() === selectedVariantId?.toString();
      const isAvailable = variant.available !== false;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `quick-add__size-button ${!isAvailable ? 'quick-add__size-button--disabled' : ''}`;
      button.dataset.variantId = variant.id;
      button.dataset.sizeValue = sizeValue;
      button.textContent = sizeValue;

      if (!isAvailable) {
        button.disabled = true;
      }

      // Ajouter l'event listener pour la sélection
      button.addEventListener('click', this.selectSize);

      sizesContainer.appendChild(button);
    });

    // Mettre à jour l'input hidden avec le variant sélectionné
    if (variantInput && selectedVariantId) {
      variantInput.value = selectedVariantId;
    }
  };

  /**
   * Gère la soumission du formulaire (ajout au panier)
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const submitButton = this.refs.trigger;
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    const spinner = submitButton?.querySelector('.quick-add__spinner') || submitButton?.querySelector('.loading-overlay__spinner');

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    spinner?.classList.remove('hidden');

    const formData = new FormData(form);

    // Ajouter les sections pour le cart drawer (comme dans product-form.js)
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      // Passer un tableau directement, pas une chaîne jointe
      formData.append('sections', cartDrawer.getSectionsToRender().map(s => s.id));
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);
    }

    // Utiliser le même format que product-form.js
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      // Utiliser routes.cart_add_url comme dans product-form.js
      const cartAddUrl = window.routes?.cart_add_url || window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, config);
      const data = await response.json();

      if (response.ok && !data.status) {
        // Fermer le panneau si ouvert
        this.closePanel();

        // Ouvrir le cart drawer
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(data);
          // Retirer is-empty car on vient d'ajouter un produit
          cartDrawer.classList.remove("is-empty");
          if (cartDrawer.open) {
            cartDrawer.open();
          }
        }
      }
    } catch (error) {
    } finally {
      submitButton?.removeAttribute('aria-disabled');
      submitButton?.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
